import { useRef, useState, type KeyboardEvent } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { MentionableMember } from "@/hooks/use-waste-event-comments";
import { useTranslation } from "@/i18n/use-translation";

const TEXTAREA_CLASS =
  "min-h-[60px] w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500";

// The active @-token immediately before the caret: "@" then word chars.
const MENTION_RE = /@(\w*)$/;
const MAX_MATCHES = 8;

type Selected = { user_id: string; name: string };

type Props = {
  members: MentionableMember[];
  isPending: boolean;
  onSubmit: (body: string, mentions: string[]) => void;
};

function memberName(m: MentionableMember): string {
  // `||` (not `??`) so an empty-string display name also falls back to email,
  // matching how the backend resolves mention names.
  return m.full_name || m.email || "Unknown";
}

/**
 * Comment compose box with @-mention autocomplete (POS2-761). Typing `@` opens a
 * picker of mentionable members; selecting inserts `@Name ` and records the id.
 * On submit only mentions whose `@Name` still appears in the body are sent.
 */
export function MentionComposer({ members, isPending, onSubmit }: Props) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState("");
  const [selected, setSelected] = useState<Selected[]>([]);
  const [query, setQuery] = useState<string | null>(null); // null = picker closed
  const [activeIndex, setActiveIndex] = useState(0);

  const matches =
    query === null
      ? []
      : members
          .filter((m) => {
            const q = query.toLowerCase();
            return (
              memberName(m).toLowerCase().includes(q) || (m.email ?? "").toLowerCase().includes(q)
            );
          })
          .slice(0, MAX_MATCHES);

  function syncQuery(value: string, caret: number) {
    const match = value.slice(0, caret).match(MENTION_RE);
    setQuery(match ? match[1] : null);
    setActiveIndex(0);
  }

  function insertMention(member: MentionableMember) {
    const el = textareaRef.current;
    const caret = el?.selectionStart ?? draft.length;
    const before = draft.slice(0, caret).replace(MENTION_RE, `@${memberName(member)} `);
    const next = before + draft.slice(caret);
    setDraft(next);
    setSelected((prev) =>
      prev.some((s) => s.user_id === member.user_id)
        ? prev
        : [...prev, { user_id: member.user_id, name: memberName(member) }],
    );
    setQuery(null);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(before.length, before.length);
    });
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (query === null || matches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + matches.length) % matches.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      insertMention(matches[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setQuery(null);
    }
  }

  function handleSubmit() {
    const body = draft.trim();
    if (!body) return;
    // Drop mentions the author deleted from the text before posting.
    const ids = Array.from(
      new Set(selected.filter((s) => draft.includes(`@${s.name}`)).map((s) => s.user_id)),
    );
    onSubmit(body, ids);
  }

  return (
    <div className="relative border-t border-gray-100 p-4">
      {query !== null && matches.length > 0 && (
        <ul
          className="absolute bottom-full left-4 right-4 mb-1 max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-md"
          data-testid="mention-picker"
        >
          {matches.map((m, i) => (
            <li key={m.user_id}>
              <button
                type="button"
                // mousedown (not click) so selecting doesn't blur the textarea first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(m);
                }}
                className={`flex w-full flex-col px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${
                  i === activeIndex ? "bg-gray-50" : ""
                }`}
                data-testid="mention-option"
              >
                <span className="text-gray-900">{memberName(m)}</span>
                {m.email && <span className="text-xs text-gray-400">{m.email}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      <textarea
        ref={textareaRef}
        className={TEXTAREA_CLASS}
        placeholder={t("waste.commentPlaceholder")}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          syncQuery(e.target.value, e.target.selectionStart ?? e.target.value.length);
        }}
        onKeyDown={handleKeyDown}
        aria-label={t("waste.addComment")}
        data-testid="comment-compose"
      />
      <div className="mt-2 flex justify-end">
        <Button
          size="sm"
          className="h-8 text-xs"
          onClick={handleSubmit}
          disabled={isPending || !draft.trim()}
          data-testid="comment-post"
        >
          {isPending ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
          ) : (
            t("waste.comment")
          )}
        </Button>
      </div>
    </div>
  );
}
